import { html, LitElement, nothing, PropertyValues } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { mixinDelegatesAria } from '@material/web/internal/aria/delegate.js';
import { repeat } from 'lit/directives/repeat.js';
import { ifDefined } from 'lit-html/directives/if-defined.js';
import '@digital-realty/ix-field/ix-field.js';
import '@digital-realty/ix-menu/ix-menu.js';
import '@digital-realty/ix-menu/ix-menu-item.js';
import '@digital-realty/ix-icon-button/ix-icon-button.js';
import '@digital-realty/ix-button';
import { IxMenu } from '@digital-realty/ix-menu';
import { IxField } from '@digital-realty/ix-field';
import {
  mixinOnReportValidity,
  onReportValidity,
} from '@material/web/labs/behaviors/on-report-validity.js';
import {
  createValidator,
  getValidityAnchor,
  mixinConstraintValidation,
} from '@material/web/labs/behaviors/constraint-validation.js';
import {
  getFormValue,
  mixinFormAssociated,
} from '@material/web/labs/behaviors/form-associated.js';
import { mixinElementInternals } from '@material/web/labs/behaviors/element-internals.js';
import { TextFieldValidator } from '@material/web/labs/behaviors/validators/text-field-validator.js';
import { Validator } from '@material/web/labs/behaviors/validators/validator.js';
import { IxFilterSelectStyles } from './ix-filter-select-styles.js';

export interface IFilterSelectOption {
  value: string;
  label: string;
}

const filterSelectBaseClass = mixinOnReportValidity(
  mixinConstraintValidation(
    mixinFormAssociated(mixinDelegatesAria(mixinElementInternals(LitElement)))
  )
);

export class IxFilterSelect extends filterSelectBaseClass {
  static get styles() {
    return [IxFilterSelectStyles];
  }

  /** @nocollapse */
  static shadowRootOptions = {
    ...LitElement.shadowRootOptions,
    delegatesFocus: true,
  };

  @query('ix-menu') public menu!: IxMenu;

  @query('input')
  readonly input!: HTMLInputElement;

  @query('.field') private readonly field!: IxField | null;

  @property({ type: Array }) options: IFilterSelectOption[] = [];

  @property({ type: String }) value: string = '';

  @property({ type: String }) label = '';

  @property({ type: String, attribute: 'error-text' }) errorText =
    'Invalid error text';

  @property({ type: String, attribute: 'no-options-text' }) noFilteredOptions =
    'No options';

  @property({ attribute: 'supporting-text' }) supportingText = '';

  @property({ type: Boolean, reflect: true }) error = false;

  @property({ type: Number, reflect: true }) tabIndex = 0;

  @property({ type: Boolean, reflect: true }) required = false;

  @property({ type: Function }) onViewMoreButtonClick: any;

  @property({ type: Boolean }) isViewMoreButtonVisible = false;

  @property({ type: Boolean }) isViewMoreButtonLoading = false;

  @property({ type: String }) viewMoreButtonLabel = 'View More';

  @property({ type: String }) placeholder?: string;

  /**
   * Used to prevent IxFilterSelect from opening the menu. This is useful
   * when options are yet to be provided via an async process.
   */
  @property({ type: Boolean }) disableMenu = false;

  /**
   * Optional property to control how the options are filtered based on value in the field.
   * If not provided, defaults to case-insensitive substring match on the label.
   */
  @property() filterFunctionGenerator?: (
    filterValue: string
  ) => (option: IFilterSelectOption) => boolean;

  /**
   * Exposed method to open and close the menu
   */
  public setMenuOpen(value: boolean) {
    this.menuOpen = value;
  }

  @state() private filteredOptions: IFilterSelectOption[] = [];

  @state() public selectedItem: IFilterSelectOption | undefined = undefined;

  @state() private filterValue = '';

  /**
   * When set, either opens or closes the menu on the next render.
   * Also reflects when menu is opened from IxMenu.
   */
  @state() private menuOpen = false;

  /**
   * Returns true when the text field has been interacted with. Native
   * validation errors only display in response to user interactions.
   */
  @state() private dirty = false;

  @state() private focused = false;

  /**
   * Whether or not a native error has been reported via `reportValidity()`.
   */
  @state() private nativeError = false;

  /**
   * The validation message displayed from a native error via
   * `reportValidity()`.
   */
  @state() private nativeErrorText = '';

  @state() private menuOpening = false;

  private get hasError() {
    return !this.dirty && (this.error || this.nativeError);
  }

  private getErrorText() {
    return this.error ? this.errorText : this.nativeErrorText;
  }

  private getInput() {
    if (!this.input) {
      // If the input is not yet defined, synchronously render.
      // e.g.
      // const textField = document.createElement('md-outlined-text-field');
      // document.body.appendChild(textField);
      // textField.focus(); // synchronously render
      this.connectedCallback();
      this.scheduleUpdate();
    }

    if (this.isUpdatePending) {
      // If there are pending updates, synchronously perform them. This ensures
      // that constraint validation properties (like `required`) are synced
      // before interacting with input APIs that depend on them.
      this.scheduleUpdate();
    }

    return this.input!;
  }

  firstUpdated() {
    this.filterOptions();
  }

  protected override updated(changedProperties: PropertyValues) {
    if (changedProperties.has('disabled')) {
      this.tabIndex = this.disabled ? -1 : 0;
    }

    if (changedProperties.has('options')) {
      this.filteredOptions = this.options;

      if (!this.selectedItem) {
        const item = this.options.find(({ value }) => value === this.value);
        this.selectItem(item);
      }
    }

    if (changedProperties.has('menuOpen')) {
      if (this.menuOpen) {
        if (this.disableMenu) {
          // We need to update menuOpen to false if we are not showing the menu
          // This will trigger a re-render with menuOpen as false which is what will actually close the menu
          this.menuOpen = false;
        } else {
          this.showMenu();
        }
      } else {
        this.menu.close();
      }
    }
  }

  handleMenuOpening = () => {
    this.menuOpening = true;
    this.menuOpen = true;
  };

  menuOpened = () => {
    this.menuOpening = false;
  };

  setMenuStyles = () => {
    const innerMenu = this.menu?.shadowRoot?.querySelector(
      '.menu'
    ) as HTMLElement;
    if (innerMenu) {
      innerMenu.style.width = '100%';
      innerMenu.style.height = 'auto';
    }
  };

  showMenu = () => {
    this.menu.show();
    this.setMenuStyles();
  };

  handleMenuClosed = () => {
    this.menuOpening = false;
    this.menuOpen = false;
  };

  handleInput = (e: InputEvent) => {
    const { value } = e.target as HTMLInputElement;
    this.dirty = true;
    this.filterValue = value;
    this.value = value;
    this.filterOptions();
  };

  selectItem = (item: IFilterSelectOption | undefined) => {
    if (item) {
      this.selectedItem = item;
      this.value = item.value;
      this.filterValue = item.label;
      this.filteredOptions = this.options;
      if (this.menuOpen || this.menuOpening) {
        this.menuOpen = false;
      }
      this.dispatchEvent(
        new CustomEvent('select-filter-select', {
          detail: { value: item.value, label: item.label },
          bubbles: true,
          composed: true,
        })
      );
    }
  };

  toggleOpen = (e: Event) => {
    e.stopPropagation();
    if (!this.menu.open) {
      this.filterOptions();
      this.menuOpen = true;
    } else {
      this.menuOpen = false;
    }
  };

  filterOptions() {
    const filterValue =
      this.filterValue.trim().toLowerCase() ||
      this.value.trim().toLocaleLowerCase();

    if (!filterValue) {
      this.filteredOptions = this.options;
      return;
    }

    const filterFunction = this.filterFunctionGenerator
      ? this.filterFunctionGenerator(filterValue)
      : (item: IFilterSelectOption) =>
          item.label.toLowerCase().includes(filterValue);

    this.filteredOptions = this.options.filter(filterFunction);
  }

  clear() {
    this.value = '';
    this.filterValue = '';
    this.selectedItem = undefined;
    this.focus();
    this.dispatchEvent(
      new CustomEvent('clear-filter-select', {
        bubbles: true,
        composed: true,
      })
    );
  }

  /** @private */
  formResetCallback() {
    this.reset();
  }

  /**
   * Reset the text field to its default value.
   */
  reset() {
    this.value = '';
    const originalValue = this.getAttribute('value');
    const item = this.options.find(({ value }) => value === originalValue);
    this.selectItem(item);
    this.dirty = false;
    this.nativeError = false;
    this.nativeErrorText = '';
  }

  override attributeChangedCallback(
    attribute: string,
    newValue: string | null,
    oldValue: string | null
  ) {
    if (attribute === 'value' && this.dirty) {
      // After user input, changing the value attribute no longer updates the
      // text field's value (until reset). This matches native <input> behavior.
      return;
    }

    super.attributeChangedCallback(attribute, newValue, oldValue);
  }

  [createValidator](): Validator<unknown> {
    return new TextFieldValidator(() => ({
      state: this as any,
      renderedControl: this.input,
    }));
  }

  [getValidityAnchor](): HTMLElement | null {
    return this.input;
  }

  [onReportValidity](invalidEvent: Event | null) {
    // Prevent default pop-up behavior.
    invalidEvent?.preventDefault();

    this.nativeError = !!invalidEvent;
    this.nativeErrorText = this.validationMessage;
  }

  // Writable mixin properties for lit-html binding, needed for lit-analyzer
  declare disabled: boolean;

  declare name: string;

  override [getFormValue]() {
    return this.value;
  }

  override formStateRestoreCallback(formState: string) {
    this.value = formState;
  }

  private handleFocusChange() {
    this.focused = this.input?.matches(':focus') ?? false;

    if (this.menuOpen || this.menuOpening) return;

    if (this.focused) {
      this.menuOpen = true;
    }
  }

  override focus() {
    // Required for the case that the user slots a focusable element into the
    // leading icon slot such as an iconbutton due to how delegatesFocus works.
    this.getInput().focus();
  }

  renderViewMoreButton() {
    if (!this.isViewMoreButtonVisible) return nothing;

    return html` <div class="filter-select-footer-section">
      <ix-button
        type="button"
        appearance="text"
        ?disabled=${this.isViewMoreButtonLoading}
        .submitting=${this.isViewMoreButtonLoading}
        @click=${() => this.onViewMoreButtonClick()}
      >
        <span class="button-label">${this.viewMoreButtonLabel}</span>
      </ix-button>
    </div>`;
  }

  render() {
    const classes = {
      disabled: this.disabled,
      error: !this.disabled && this.hasError,
    };

    return html`
      <div class="filter-select">
        <ix-field
          class="${classMap(classes)}"
          id="anchor"
          ?focused=${this.focused}
          ?disabled=${this.disabled}
          ?required=${this.required}
          ?error=${this.hasError}
          error-text=${!this.menuOpen && !this.menuOpening
            ? this.getErrorText()
            : nothing}
          label=${this.label}
          @click=${() => (!this.disabled ? this.input.focus() : null)}
          supporting-text=${this.supportingText}
          ?populated=${this.filterValue.length || this.value.length}
        >
          <input
            id="filter"
            aria-invalid=${this.hasError}
            @input=${this.handleInput}
            .value=${this.filterValue}
            class="flex-fill input"
            type="text"
            ?disabled=${this.disabled}
            ?required=${this.required}
            @focus=${this.handleFocusChange}
            @blur=${this.handleFocusChange}
            autocomplete="off"
            placeholder=${ifDefined(this.placeholder)}
          />
          <slot name="end" slot="end">
            ${this.value.length
              ? html`<ix-icon-button
                  @click=${this.clear}
                  icon="close"
                  aria-label="clear"
                ></ix-icon-button>`
              : nothing}
            <ix-icon-button
              @click=${this.toggleOpen}
              class="open-icon"
              icon=${`arrow_drop_${this.menuOpen ? 'up' : 'down'}`}
              aria-label="options"
            ></ix-icon-button>
          </slot>
        </ix-field>
        <div class="menu">
          <ix-menu
            anchor="anchor"
            @opening=${this.handleMenuOpening}
            @opened=${this.menuOpened}
            @closed=${this.handleMenuClosed}
            default-focus="none"
            skip-restore-focus
            anchor-corner="start-start"
            surface-corner="start-start"
            y-offset=${this.supportingText
              ? -parseFloat(getComputedStyle(document.documentElement).fontSize)
              : 0}
          >
            ${this.filteredOptions?.length
              ? repeat(
                  this.filteredOptions,
                  (item, index) => `${item.value}-${index}`,
                  item =>
                    html`<ix-menu-item
                      @click=${() => {
                        this.selectItem(item);
                      }}
                      @keydown=${(e: KeyboardEvent) => {
                        const selectionKeys = ['Space', 'Enter'];
                        if (selectionKeys.includes(e.key)) {
                          this.selectItem(item);
                        }
                      }}
                      class=${this.value === item.value ? 'selected' : ''}
                    >
                      <div slot="headline">${item.label}</div>
                    </ix-menu-item>`
                )
              : html`<ix-menu-item>${this.noFilteredOptions}</ix-menu-item>`}
            ${this.renderViewMoreButton()}
          </ix-menu>
        </div>
      </div>
    `;
  }
}
